import type { ReactElement } from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { PaymentStatus, SubscriptionStatus } from '@cookspace-shared-contracts/core';
import { ArrowLeft } from 'lucide-react';

import { useListPaymentsQuery } from '@/api/paymentsApi';
import {
    useCancelSubscriptionMutation,
    useExtendSubscriptionMutation,
    useGetSubscriptionQuery
} from '@/api/subscriptionsApi';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import type { AdminSubscriptionListItem } from '@/lib/subscriptionsSchemas';

const REASON_MIN_LENGTH = 10;

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
    pending: 'Ожидает',
    active: 'Активна',
    grace_period: 'Grace',
    expired: 'Истекла',
    cancelled: 'Отменена'
};

const STATUS_COLOR: Record<SubscriptionStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    grace_period: 'bg-orange-100 text-orange-800',
    expired: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-red-100 text-red-800'
};

const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
    pending: 'Ожидает',
    succeeded: 'Оплачен',
    cancelled: 'Отменён',
    failed: 'Ошибка',
    refunded: 'Возврат'
};

const PAYMENT_STATUS_COLOR: Record<PaymentStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    succeeded: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-700',
    failed: 'bg-red-100 text-red-800',
    refunded: 'bg-blue-100 text-blue-800'
};

const PRICE_FORMATTER = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
});

function formatDate(iso: string | null): string {
    if (iso === null) {
        return '—';
    }
    return new Date(iso).toLocaleString('ru-RU');
}

function formatPrice(rub: number): string {
    return PRICE_FORMATTER.format(rub);
}

function formatUserContact(sub: AdminSubscriptionListItem): string {
    const { user } = sub;
    if (user === null) {
        return sub.userId.slice(0, 8);
    }
    if (user.email !== null && user.email !== '') {
        return user.email;
    }
    if (user.phoneNumber !== null && user.phoneNumber !== '') {
        return user.phoneNumber;
    }
    return user.displayName ?? user.id.slice(0, 8);
}

export function SubscriptionDetailPage(): ReactElement {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const subscriptionQuery = useGetSubscriptionQuery(
        id !== undefined ? { id } : { id: '' },
        { skip: id === undefined || id === '' }
    );
    const paymentsQuery = useListPaymentsQuery(
        subscriptionQuery.data?.subscription.userId !== undefined
            ? { userId: subscriptionQuery.data.subscription.userId, limit: 50 }
            : undefined,
        { skip: subscriptionQuery.data === undefined }
    );

    const [cancelSubscription, { isLoading: isCancelling }] = useCancelSubscriptionMutation();
    const [extendSubscription, { isLoading: isExtending }] = useExtendSubscriptionMutation();

    const [isCancelOpen, setCancelOpen] = useState(false);
    const [cancelImmediate, setCancelImmediate] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelReasonError, setCancelReasonError] = useState<string | null>(null);
    const [isExtendOpen, setExtendOpen] = useState(false);
    const [extendDays, setExtendDays] = useState('30');
    const [extendDaysError, setExtendDaysError] = useState<string | null>(null);
    const [extendReason, setExtendReason] = useState('');
    const [extendReasonError, setExtendReasonError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    if (id === undefined || id === '') {
        return (
            <div className="text-sm text-red-600" data-testid="subscription-detail-bad-id">
                Не указан ID подписки
            </div>
        );
    }

    if (subscriptionQuery.isLoading) {
        return <div className="text-sm text-gray-500">Загрузка подписки...</div>;
    }

    if (subscriptionQuery.isError || subscriptionQuery.data === undefined) {
        return (
            <div className="text-sm text-red-600" data-testid="subscription-detail-error">
                Не удалось загрузить подписку
            </div>
        );
    }

    const sub = subscriptionQuery.data.subscription;
    const payments = paymentsQuery.data?.items ?? [];

    const closeCancel = (): void => {
        setCancelOpen(false);
        setCancelReason('');
        setCancelReasonError(null);
    };

    const closeExtend = (): void => {
        setExtendOpen(false);
        setExtendDays('30');
        setExtendDaysError(null);
        setExtendReason('');
        setExtendReasonError(null);
    };

    const handleCancel = async (): Promise<void> => {
        const reasonTrimmed = cancelReason.trim();
        if (reasonTrimmed.length < REASON_MIN_LENGTH) {
            setCancelReasonError(`Минимум ${REASON_MIN_LENGTH} символов`);
            return;
        }
        setCancelReasonError(null);

        try {
            await cancelSubscription({
                id: sub.id,
                body: { reason: reasonTrimmed, immediate: cancelImmediate }
            }).unwrap();
            closeCancel();
        } catch {
            setActionError('Не удалось отменить подписку');
            closeCancel();
        }
    };

    const handleExtend = async (): Promise<void> => {
        const days = Number(extendDays);
        let hasError = false;

        if (!Number.isInteger(days) || days < 1 || days > 365) {
            setExtendDaysError('Количество дней — целое число от 1 до 365');
            hasError = true;
        } else {
            setExtendDaysError(null);
        }

        const reasonTrimmed = extendReason.trim();
        if (reasonTrimmed.length < REASON_MIN_LENGTH) {
            setExtendReasonError(`Минимум ${REASON_MIN_LENGTH} символов`);
            hasError = true;
        } else {
            setExtendReasonError(null);
        }

        if (hasError) {
            return;
        }

        try {
            await extendSubscription({
                id: sub.id,
                body: { days, reason: reasonTrimmed }
            }).unwrap();
            closeExtend();
        } catch {
            setActionError('Не удалось продлить подписку');
            closeExtend();
        }
    };

    return (
        <div data-testid="subscription-detail-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => navigate('/subscriptions')}
                    className="inline-flex items-center gap-1 text-sm text-gray-600 transition hover:text-gray-900"
                    data-testid="subscription-detail-back"
                >
                    <ArrowLeft size={16} />
                    К списку
                </button>
            </div>

            {actionError !== null && actionError !== '' && (
                <div
                    className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="subscription-detail-action-error"
                >
                    {actionError}
                </div>
            )}

            <div className="rounded border border-gray-200 bg-white p-5">
                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            Подписка {sub.id.slice(0, 8)}
                        </h1>
                        <p className="mt-1 text-sm text-gray-600">
                            Пользователь: {formatUserContact(sub)}
                        </p>
                    </div>
                    <span
                        className={`inline-block rounded px-2 py-1 text-xs font-medium ${STATUS_COLOR[sub.status]}`}
                        data-testid="subscription-detail-status"
                    >
                        {STATUS_LABEL[sub.status]}
                    </span>
                </div>

                <dl
                    className="mt-5 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2"
                    data-testid="subscription-detail-fields"
                >
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">Тариф</dt>
                        <dd className="text-gray-900">
                            {sub.tariff !== null
                                ? `${sub.tariff.name} · ${formatPrice(sub.tariff.priceRub)} · ${String(sub.tariff.durationDays)} дн.`
                                : '—'}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">Провайдер</dt>
                        <dd className="text-gray-900">{sub.provider}</dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">Создана</dt>
                        <dd className="text-gray-900">{formatDate(sub.createdAt)}</dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">Активна с</dt>
                        <dd className="text-gray-900">{formatDate(sub.startedAt)}</dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">
                            Период с
                        </dt>
                        <dd className="text-gray-900">{formatDate(sub.currentPeriodStart)}</dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">
                            Период до
                        </dt>
                        <dd className="text-gray-900">{formatDate(sub.currentPeriodEnd)}</dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">
                            Отменена
                        </dt>
                        <dd className="text-gray-900">{formatDate(sub.cancelledAt)}</dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">
                            Отмена в конце периода
                        </dt>
                        <dd className="text-gray-900">{sub.cancelAtPeriodEnd ? 'Да' : 'Нет'}</dd>
                    </div>
                </dl>
            </div>

            <div className="rounded border border-gray-200 bg-white p-5">
                <h2 className="mb-3 text-base font-semibold text-gray-900">
                    История платежей пользователя ({payments.length})
                </h2>
                {paymentsQuery.isLoading && (
                    <div className="text-sm text-gray-500">Загрузка платежей...</div>
                )}
                {paymentsQuery.isError && (
                    <div className="text-sm text-red-600">Не удалось загрузить платежи</div>
                )}
                {!paymentsQuery.isLoading && !paymentsQuery.isError && payments.length === 0 && (
                    <div className="text-sm text-gray-500">Платежей нет</div>
                )}
                {payments.length > 0 && (
                    <div data-testid="subscription-detail-payments">
                        <div className="grid grid-cols-[80px_minmax(0,1fr)_120px_120px_140px_100px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                            <span>ID</span>
                            <span>Описание</span>
                            <span>Сумма</span>
                            <span>Статус</span>
                            <span>Оплачен</span>
                            <span>Возврат</span>
                        </div>
                        {payments.map((p) => (
                            <div
                                key={p.id}
                                className="grid grid-cols-[80px_minmax(0,1fr)_120px_120px_140px_100px] items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                                data-testid={`subscription-detail-payment-${p.id}`}
                            >
                                <span className="truncate font-mono text-xs text-gray-500">
                                    {p.id.slice(0, 8)}
                                </span>
                                <span className="truncate text-gray-700">
                                    {p.description ?? '—'}
                                </span>
                                <span className="text-gray-900">{formatPrice(p.amountRub)}</span>
                                <span>
                                    <span
                                        className={`inline-block rounded px-2 py-0.5 text-xs ${PAYMENT_STATUS_COLOR[p.status]}`}
                                    >
                                        {PAYMENT_STATUS_LABEL[p.status]}
                                    </span>
                                </span>
                                <span className="text-xs text-gray-700">
                                    {formatDate(p.paidAt)}
                                </span>
                                <span className="text-xs text-gray-700">
                                    {p.refundedAmountRub !== null
                                        ? formatPrice(p.refundedAmountRub)
                                        : '—'}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="sticky bottom-0 -mx-6 flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-3">
                {(sub.status === 'active' || sub.status === 'grace_period') && (
                    <button
                        type="button"
                        onClick={() => setExtendOpen(true)}
                        disabled={isExtending}
                        data-testid="subscription-detail-extend-button"
                        className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:opacity-50"
                    >
                        Продлить
                    </button>
                )}
                {(sub.status === 'active' ||
                    sub.status === 'grace_period' ||
                    sub.status === 'pending') && (
                    <button
                        type="button"
                        onClick={() => setCancelOpen(true)}
                        disabled={isCancelling}
                        data-testid="subscription-detail-cancel-button"
                        className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
                    >
                        Отменить подписку
                    </button>
                )}
            </div>

            <Modal
                isOpen={isCancelOpen}
                onClose={closeCancel}
                title="Отмена подписки"
                testId="subscription-detail-cancel-modal"
            >
                <div className="space-y-4">
                    <div className="space-y-2 rounded border border-gray-200 p-3">
                        <label className="flex items-start gap-3 text-sm">
                            <input
                                type="radio"
                                name="detailCancelMode"
                                checked={!cancelImmediate}
                                onChange={() => setCancelImmediate(false)}
                                data-testid="subscription-detail-cancel-period-end"
                                className="mt-0.5"
                            />
                            <span>
                                <span className="font-medium text-gray-900">
                                    В конце оплаченного периода
                                </span>
                                <span className="block text-xs text-gray-600">
                                    Доступ сохранится до {formatDate(sub.currentPeriodEnd)}
                                </span>
                            </span>
                        </label>
                        <label className="flex items-start gap-3 text-sm">
                            <input
                                type="radio"
                                name="detailCancelMode"
                                checked={cancelImmediate}
                                onChange={() => setCancelImmediate(true)}
                                data-testid="subscription-detail-cancel-immediate"
                                className="mt-0.5"
                            />
                            <span>
                                <span className="font-medium text-gray-900">Немедленно</span>
                                <span className="block text-xs text-gray-600">
                                    Доступ закроется сразу.
                                </span>
                            </span>
                        </label>
                    </div>

                    <Textarea
                        label={`Причина отмены (минимум ${REASON_MIN_LENGTH} символов)`}
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={4}
                        error={cancelReasonError ?? undefined}
                        data-testid="subscription-detail-cancel-reason"
                    />

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={closeCancel}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                void handleCancel();
                            }}
                            disabled={isCancelling}
                            data-testid="subscription-detail-cancel-submit"
                            className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
                        >
                            Отменить подписку
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={isExtendOpen}
                onClose={closeExtend}
                title="Продлить подписку"
                testId="subscription-detail-extend-modal"
            >
                <div className="space-y-4">
                    <Input
                        label="Количество дней"
                        type="number"
                        value={extendDays}
                        onChange={(e) => setExtendDays(e.target.value)}
                        error={extendDaysError ?? undefined}
                        data-testid="subscription-detail-extend-days"
                        autoFocus
                    />
                    <Textarea
                        label={`Причина продления (минимум ${REASON_MIN_LENGTH} символов)`}
                        value={extendReason}
                        onChange={(e) => setExtendReason(e.target.value)}
                        rows={4}
                        error={extendReasonError ?? undefined}
                        data-testid="subscription-detail-extend-reason"
                    />
                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={closeExtend}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                void handleExtend();
                            }}
                            disabled={isExtending}
                            data-testid="subscription-detail-extend-submit"
                            className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                        >
                            Продлить
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
